import { useEffect, useRef, useState } from 'react'

import InfiniteScroll from 'react-infinite-scroller'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import {
  StoreItemStatus,
  Template,
  TemplateEntityType,
} from '@tribeplatform/gql-client/types'
import { useTracker } from '@tribeplatform/react-sdk'
import { useTemplates } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { EntityImage } from '../common/components/EntityImage.js'
import { useDebounce } from '../common/hooks/useDebounce.js'
import { T } from '../i18n/index.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

import { LoadingState } from './components/LoadingState.js'

export const TemplateSidebar = () => {
  const { $t } = useI18n()
  const [query, setQuery] = useState('')
  const { search } = useLocation()
  const navigate = useNavigate()
  const { templateId } = useParams<{ templateId?: string }>()
  const debouncedQuery = useDebounce(query, 300)
  const { tracker } = useTracker()
  const ref = useRef<HTMLDivElement>(null)
  const { data, fetchNextPage, hasNextPage, isInitialLoading } = useTemplates({
    fields: {
      banner: {
        onEmoji: 'all',
        onImage: 'all',
        onGlyph: 'all',
      },
    },
    variables: {
      entityTypes: [TemplateEntityType.Space],
      query: debouncedQuery,
      limit: 10,
    },
    useInfiniteQueryOptions: {
      keepPreviousData: true,
    },
  })

  const { nodes: templates } = simplifyPaginatedResult<Template>(data)
  const spaceTemplates = templates.filter(t => t.name !== 'Private Message')

  useEffect(() => {
    if (templateId) {
      tracker.track('Select Template', {
        templateId,
      })
    }
  }, [templateId, tracker])

  useEffect(() => {
    if (spaceTemplates.length > 0 && !templateId) {
      const [firstTemplate] = spaceTemplates
      navigate(`${adminRoutes.design.createSpace(firstTemplate.id)}${search}`, {
        replace: true,
      })
    }
  }, [navigate, search, spaceTemplates, templateId])

  return (
    <div className="flex flex-col gap-3 h-full">
      <div className="space-y-3">
        <FormControl.Input
          leadingIcon={<SvgIcon name="search" />}
          placeholder={$t({
            defaultMessage: 'Search',
            id: 'Generics.Search',
          })}
          value={query}
          onChange={e => setQuery(e.target.value)}
          name="query"
        />
      </div>
      <div className="flex-1 overflow-auto min-w-0" ref={ref}>
        <NavVertical>
          <NavVertical.Group>
            <InfiniteScroll
              pageStart={0}
              loadMore={fetchNextPage}
              hasMore={hasNextPage || false}
              threshold={800}
              useWindow={false}
              getScrollParent={() => ref.current}
              loader={<LoadingState />}
              className="space-y-2"
            >
              {isInitialLoading && <LoadingState />}
              {spaceTemplates.map(template => (
                <NavVertical.Item
                  key={template.id}
                  as={Link}
                  to={`${adminRoutes.design.createSpace(template.id)}${search}`}
                  active={templateId === template.id}
                  leadingIcon={
                    <EntityImage
                      entity={{ image: template.banner }}
                      size="xl"
                      className="!-ms-px"
                    />
                  }
                >
                  <div className="truncate">
                    {template.name}
                    {template.status === StoreItemStatus.PRIVATE && (
                      <Tooltip>
                        <Tooltip.Trigger className="truncate inline">
                          <SvgIcon name="eye-off" className="ms-1" inline />
                        </Tooltip.Trigger>
                        <Tooltip.Panel>
                          <T
                            defaultMessage="This template is internal only."
                            id="TemplateSidebar.Template.InternalOnly"
                          />
                        </Tooltip.Panel>
                      </Tooltip>
                    )}
                  </div>
                </NavVertical.Item>
              ))}
            </InfiniteScroll>
          </NavVertical.Group>
        </NavVertical>
      </div>
    </div>
  )
}
